import type { MenuItem as MenuItemType } from "@v1/components/designSystem/menu/Menu";
import type { LeftBarTab } from "@v1/reducers/ui-reducer";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { CanvasData } from "./canvas-types";

import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import DeprecatedIconButton from "@v1/components/designSystem/button/IconButton";
import { HotkeyIndicator } from "@v1/components/designSystem/hotkeys/HotKeyIndicator";
import { Menu } from "@v1/components/designSystem/menu/Menu";
import { MenuItem } from "@v1/components/designSystem/menu/MenuItem";
import Popover from "@v1/components/designSystem/popover/Popover";
import Selectable from "@v1/components/designSystem/Selectable";
import { BuildAssistant } from "@v1/components/editor/ai/build-assistant/BuildAssistant";
import { LengthInputSelector } from "@v1/components/editor/page/element-editor/components/modifiers/LengthInputModifier";
import { JSONActionPlayer } from "@v1/components/JSONActionPlayer";
import { useSelectedArchivedElement } from "@v1/hooks/useSelectedArchivedElement";
import { useUnarchiveElement } from "@v1/hooks/useUnarchiveElement";
import { useV1AIStreaming } from "@v1/providers/AIStreamingProvider";
import {
  redoOperation,
  selectEditorMode,
  selectIsPreviewMode,
  selectValidNextOperation,
  selectValidPreviousOperation,
  setEditorMode,
  undoOperation,
} from "@v1/reducers/core-reducer";
import {
  selectLeftBarActiveTab,
  setLeftBarActiveTab,
} from "@v1/reducers/ui-reducer";
import {
  useEditorDispatch,
  useEditorSelector,
  useEditorStore,
} from "@v1/store/store";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  CirclePlay,
  Monitor,
  Proportions,
  Redo2,
  Smartphone,
  SquareArrowLeft,
  Tablet,
  Undo2,
} from "lucide-react";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";
import { parseFloat } from "replo-utils/lib/math";
import { capitalizeFirstLetter } from "replo-utils/lib/string";

import { CANVAS_DATA } from "./canvas-constants";
import {
  selectActiveCanvas,
  selectActiveCanvasFrameWidth,
  selectCanvasFrameWidths,
  selectCanvasScale,
  selectPreviewWidth,
  setPreviewWidth,
} from "./canvas-reducer";
import { getCanvasData, getPresets } from "./canvas-utils";
import { useCanvasZoom } from "./useCanvasZoom";
import { useDeviceControls } from "./useDeviceControls";

// Constants
const SCALE_OPTIONS = [
  { label: "50%", value: "0.5" },
  { label: "75%", value: "0.75" },
  { label: "100%", value: "1.0" },
  { label: "125%", value: "1.25" },
];

const SCALE_ACTIONS = [
  { label: "Zoom In", value: "zoomIn" },
  { label: "Zoom Out", value: "zoomOut" },
];

// Shared Components
type ControlsContainerProps = React.PropsWithChildren<{
  className?: string;
}>;

const ControlsContainer = React.forwardRef<
  HTMLDivElement,
  ControlsContainerProps
>(({ children, className }, ref) => (
  <aside
    aria-label="Canvas Controls"
    className={twMerge(
      "shadow-lg border-0.5 border-border fixed bottom-0 left-1/2 flex w-96 -translate-x-1/2 -translate-y-6 items-center rounded-xl bg-white p-2",
      className,
    )}
    ref={ref}
  >
    {children}
  </aside>
));

ControlsContainer.displayName = "ControlsContainer";

export const CanvasControls: React.FC = () => {
  const [isScaleMenuVisible, setIsScaleMenuVisible] = React.useState(false);
  const [previousLeftBarTab, setPreviousLeftBarTab] =
    React.useState<LeftBarTab | null>(null);

  const store = useEditorStore();
  const dispatch = useEditorDispatch();
  const logEvent = useLogAnalytics();
  const {
    abort: abortAIStream,
    isBuildAssistantMinimized,
    setIsBuildAssistantMinimized,
    status,
  } = useV1AIStreaming();

  const isGenerating =
    status === "generating" || status === "generationInitialized";

  // Selectors
  const editorMode = useEditorSelector(selectEditorMode);
  const canvasScale = useEditorSelector(selectCanvasScale);
  const hasPreviousOperation = useEditorSelector(selectValidPreviousOperation);
  const hasNextOperation = useEditorSelector(selectValidNextOperation);

  const archivedElement = useSelectedArchivedElement();

  // Hooks
  const { handleCanvasZoom, handleCanvasZoomIn, handleCanvasZoomOut } =
    useCanvasZoom();
  const { unarchiveElement, isLoading: isUnarchiveLoading } =
    useUnarchiveElement();

  // Derived state
  const isEditMode = editorMode === "edit" || editorMode === "aiGeneration";
  const formattedCanvasScale = `${(canvasScale * 100).toFixed(0)}%`;
  const areControlsDisabled = editorMode === "aiGeneration";

  // Event handlers
  function handleEditorModeChange() {
    const activeCanvasFrameWidth = selectActiveCanvasFrameWidth(
      store.getState(),
    );

    if (editorMode === "versioning" || editorMode === "archived") {
      dispatch(setEditorMode("edit"));
      return;
    }

    // Cancel AI streaming when switching back to edit mode
    if (editorMode === "aiGeneration") {
      abortAIStream();
    }

    if (isEditMode) {
      logEvent("canvas.preview", { source: "toolbar" });
      dispatch(setPreviewWidth(activeCanvasFrameWidth));
    }

    if (isEditMode) {
      setPreviousLeftBarTab(selectLeftBarActiveTab(store.getState()));
      dispatch(setLeftBarActiveTab(null));
      dispatch(setEditorMode("preview"));
    } else {
      dispatch(setLeftBarActiveTab(previousLeftBarTab ?? "components"));
      setPreviousLeftBarTab(null);
      dispatch(setEditorMode("edit"));
    }
  }

  function handleUndo() {
    dispatch(undoOperation());
  }

  function handleRedo() {
    dispatch(redoOperation());
  }

  function handleMinimizeBuildAssistantButtonClick() {
    if (isBuildAssistantMinimized) {
      logEvent("ai.build-assistant.expand");
      setIsBuildAssistantMinimized(false);
    } else {
      setIsBuildAssistantMinimized(true);
      logEvent("ai.build-assistant.minimize");
    }
  }

  function handleScaleChange(value: string) {
    if (value === "zoomIn") {
      handleCanvasZoomIn();
    } else if (value === "zoomOut") {
      handleCanvasZoomOut();
    } else {
      const scale = parseFloat(value);
      if (!Number.isNaN(scale)) {
        handleCanvasZoom(scale);
      }
    }
  }

  // Scale menu items
  const scaleItems = [
    ...SCALE_OPTIONS.map(({ label, value }, idx) => ({
      type: "leaf" as const,
      variant: "default" as const,
      size: "sm" as const,
      id: idx.toString(),
      title: label,
      onSelect: () => handleScaleChange(value),
    })),
    {
      type: "section" as const,
      items: SCALE_ACTIONS.map(({ label, value }, idx) => ({
        type: "leaf" as const,
        variant: "command" as const,
        size: "sm" as const,
        id: idx.toString(),
        title: label,
        onSelect: () => handleScaleChange(value),
        shortcut: value,
      })),
    },
  ];

  // Render different controls based on editor mode
  if (editorMode === "archived") {
    return (
      <ArchivedModeControls
        archivedElement={archivedElement}
        areControlsDisabled={areControlsDisabled}
        handleEditorModeChange={handleEditorModeChange}
        unarchiveElement={unarchiveElement}
        isUnarchiveLoading={isUnarchiveLoading}
      />
    );
  }

  if (editorMode === "versioning") {
    return null;
  }

  const jsonActionPlayer = JSON.parse(
    localStorage.getItem("replo.debug.jsonActionPlayer") ?? "false",
  );

  // Default controls (edit/preview mode)
  return (
    <aside
      aria-label="Canvas Controls"
      className="shadow-lg border-0.5 border-border fixed bottom-0 left-1/2 flex w-96 -translate-x-1/2 -translate-y-6 items-center overflow-hidden rounded-xl bg-white"
    >
      {isEditMode && (
        <div className="flex w-full flex-col">
          <BuildAssistant isMinimized={isBuildAssistantMinimized} />
          <div className="bg-light-sheet border-t-0.5 border-border flex w-full justify-between p-3">
            <div className="flex gap-1.5">
              <DeprecatedIconButton
                variant="secondary"
                size="sm"
                icon={<Undo2 size={14} className="text-muted" />}
                tooltipText={<HotkeyIndicator hotkey="undo" />}
                onClick={handleUndo}
                disabled={!hasPreviousOperation || isGenerating}
              />
              <DeprecatedIconButton
                variant="secondary"
                size="sm"
                icon={<Redo2 size={14} className="text-default" />}
                tooltipText={<HotkeyIndicator hotkey="redo" />}
                onClick={handleRedo}
                disabled={!hasNextOperation || isGenerating}
              />
            </div>

            <div className="flex gap-2">
              {jsonActionPlayer && (
                <Popover
                  shouldPreventDefaultOnInteractOutside={false}
                  triggerAsChild
                  side="top"
                  className="w-[400px]"
                  content={<JSONActionPlayer />}
                >
                  <DeprecatedButton variant="secondary">
                    Actions
                  </DeprecatedButton>
                </Popover>
              )}
              <DeprecatedButton
                variant="secondary"
                size="sm"
                endEnhancer={<CirclePlay className="text-muted" size={12} />}
                disabled={areControlsDisabled}
                tooltipText={<HotkeyIndicator hotkey="togglePreviewMode" />}
                onClick={handleEditorModeChange}
                data-testid="preview-button"
              >
                <span className="text-muted">View</span>
              </DeprecatedButton>

              <ScaleMenu
                formattedCanvasScale={formattedCanvasScale}
                isScaleMenuVisible={isScaleMenuVisible}
                setIsScaleMenuVisible={setIsScaleMenuVisible}
                scaleItems={scaleItems}
              />

              <DeprecatedIconButton
                onClick={handleMinimizeBuildAssistantButtonClick}
                variant="secondary"
                size="sm"
                icon={
                  <ChevronDown
                    size={12}
                    className={twMerge(
                      "text-muted transition-all",
                      isBuildAssistantMinimized && "rotate-180",
                    )}
                  />
                }
              />
            </div>
          </div>
        </div>
      )}
      {editorMode === "preview" && (
        <div className="flex w-full justify-between p-3">
          <div className="flex gap-2">
            {Object.values(CANVAS_DATA).map((canvasData) => (
              <CanvasDeviceControls
                key={canvasData.canvasName}
                canvasData={canvasData}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <DeprecatedButton
              variant="secondary"
              size="sm"
              startEnhancer={<ChevronLeft size={12} className="text-muted" />}
              disabled={areControlsDisabled}
              tooltipText={<HotkeyIndicator hotkey="togglePreviewMode" />}
              onClick={handleEditorModeChange}
              data-testid="edit-button"
            >
              <span className="text-muted">Back</span>
            </DeprecatedButton>

            <CustomViewportControl />
          </div>
        </div>
      )}
    </aside>
  );
};

// Mode-specific Controls Components
interface ArchivedModeControlsProps {
  archivedElement: ReturnType<typeof useSelectedArchivedElement>;
  areControlsDisabled: boolean;
  handleEditorModeChange: () => void;
  unarchiveElement: ReturnType<typeof useUnarchiveElement>["unarchiveElement"];
  isUnarchiveLoading: boolean;
}

const ArchivedModeControls: React.FC<ArchivedModeControlsProps> = ({
  archivedElement,
  areControlsDisabled,
  handleEditorModeChange,
  unarchiveElement,
  isUnarchiveLoading,
}) => (
  <ControlsContainer className="px-2">
    <DeprecatedIconButton
      id="edit-button"
      variant="tertiary"
      size="lg"
      layoutClassName="h-8 w-8"
      icon={<SquareArrowLeft size={24} className="text-xl" />}
      disabled={areControlsDisabled}
      tooltipText="Exit Archive"
      onClick={handleEditorModeChange}
      data-testid="edit-button"
    />

    <Separator orientation="vertical" className="h-6" />

    <p className="text-default flex items-center gap-2">
      <span className="text-sm font-medium">{archivedElement?.name}</span>
      <Separator orientation="vertical" className="bg-default h-6" />
      {archivedElement && (
        <span className="text-sm">
          Archived{" "}
          {new Date(archivedElement?.archivedAt ?? "").toLocaleDateString()}
        </span>
      )}
    </p>

    <DeprecatedButton
      variant="primary"
      size="base"
      onClick={() => {
        if (!archivedElement?.id) {
          return;
        }
        unarchiveElement(archivedElement, { from: "button" });
      }}
      disabled={isUnarchiveLoading || !archivedElement?.id}
      isLoading={isUnarchiveLoading}
    >
      Unarchive
    </DeprecatedButton>
  </ControlsContainer>
);

// UI Components
interface ScaleMenuProps {
  formattedCanvasScale: string;
  isScaleMenuVisible: boolean;
  setIsScaleMenuVisible: (isVisible: boolean) => void;
  scaleItems: MenuItemType[];
}

const ScaleMenu: React.FC<ScaleMenuProps> = ({
  formattedCanvasScale,
  isScaleMenuVisible,
  setIsScaleMenuVisible,
  scaleItems,
}) => (
  <Menu
    items={scaleItems}
    customWidth={180}
    align="center"
    onRequestClose={() => setIsScaleMenuVisible(false)}
    onRequestOpen={() => setIsScaleMenuVisible(true)}
    triggerAsChild
    trigger={
      <DeprecatedButton
        variant="secondary"
        size="sm"
        endEnhancer={
          <ChevronDown
            size={12}
            className={twMerge(
              "text-muted transition-all",
              isScaleMenuVisible && "rotate-180",
            )}
          />
        }
      >
        <span className="text-muted">{formattedCanvasScale}</span>
      </DeprecatedButton>
    }
  />
);

interface CanvasDeviceControlsProps {
  canvasData: CanvasData;
}

const CanvasDeviceControls: React.FC<CanvasDeviceControlsProps> = ({
  canvasData,
}) => {
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const isPreviewMode = useEditorSelector(selectIsPreviewMode);
  const previewWidth = useEditorSelector(selectPreviewWidth);
  const { handleDeviceChange } = useDeviceControls();
  const Icon = getCanvasIconComponent(canvasData.canvasName);

  let isActive = activeCanvas === canvasData.canvasName;
  if (isPreviewMode) {
    const previewData = getCanvasData(previewWidth);
    isActive = previewData?.canvasName === canvasData.canvasName;
  }

  return (
    <DeprecatedIconButton
      key={canvasData.canvasName}
      variant="tertiary"
      id="page-settings-button"
      layoutClassName="h-6 w-6"
      tooltipText={capitalizeFirstLetter(canvasData.canvasName)}
      icon={
        <Icon
          className={twMerge(
            "text-xl",
            isActive ? "text-default" : "text-muted",
          )}
        />
      }
      onClick={() => handleDeviceChange(canvasData)}
    />
  );
};

interface CustomViewportControlProps {
  canvas?: EditorCanvas;
}

export const CustomViewportControl: React.FC<CustomViewportControlProps> = ({
  canvas,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const visibleCanvasesFrameWidth = useEditorSelector(selectCanvasFrameWidths);
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const isPreviewMode = useEditorSelector(selectIsPreviewMode);
  const previewWidth = useEditorSelector(selectPreviewWidth);

  const { handleViewportChange, handlePresetDeviceChange } =
    useDeviceControls();

  const currentCanvas = canvas ?? activeCanvas;
  const presets = getPresets(canvas ?? undefined);
  const canvasData = getFromRecordOrNull(CANVAS_DATA, canvas);
  const width = visibleCanvasesFrameWidth[currentCanvas];

  const isOnCanvasNavbar = Boolean(canvas);
  const selectedPreset = presets.find(
    (preset) => preset.value === (isPreviewMode ? previewWidth : width),
  );
  const presetTriggerStartEnhancer = (() => {
    if (!selectedPreset) {
      return null;
    }

    const Icon = getCanvasIconComponent(selectedPreset.canvas);
    return <Icon className="text-muted" size={14} />;
  })();

  return (
    <Popover
      triggerAsChild
      isOpen={isPopoverOpen}
      shouldPreventDefaultOnInteractOutside={false}
      onOpenChange={(isOpen) => setIsPopoverOpen(isOpen)}
      style={{ marginTop: isOnCanvasNavbar ? 8 : undefined }}
      content={
        <div className="flex flex-col gap-1 pb-3">
          <h4 className="text-muted text-xs font-normal">Set Preview Width</h4>
          <LengthInputSelector
            field="default"
            startEnhancer={<Proportions size={14} />}
            value={`${isPreviewMode ? previewWidth : width}px`}
            metrics={["px"]}
            onChange={(value) => {
              handleViewportChange(value, canvas);
            }}
            draggingType="vertical"
            draggingDirection="positive"
            minDragValues={{ px: canvasData?.range[0] ?? 0 }}
            minValues={{ px: canvasData?.range[0] ?? 0 }}
            maxDragValues={{
              px: canvasData?.range[1] ?? Number.POSITIVE_INFINITY,
            }}
            maxValues={{ px: canvasData?.range[1] ?? Number.POSITIVE_INFINITY }}
            allowsNegativeValue={false}
            dragTrigger="startEnhancer"
          />
          <h4 className="text-muted text-xs font-normal">By Device</h4>
          <Selectable
            placeholder="Select..."
            ignoreValueMismatchError
            onSelect={(value) => {
              if (!value) {
                return;
              }
              handlePresetDeviceChange(value, canvas);
            }}
            triggerStartEnhancer={presetTriggerStartEnhancer}
            value={selectedPreset?.value ? String(selectedPreset.value) : null}
            options={presets.map(({ canvas, label, value }) => {
              const Icon = getCanvasIconComponent(canvas);
              return {
                label: (
                  <MenuItem
                    variant="default"
                    selected={selectedPreset?.value === value}
                    startEnhancer={<Icon className="text-muted" size={14} />}
                  >
                    <div className="text-default flex w-full shrink-0 items-center gap-2 p-1 text-xs">
                      <span className="truncate font-normal">{label}</span>
                      <span className="text-muted">({value}px)</span>
                    </div>
                  </MenuItem>
                ),
                displayValue: `${label} (${value}px)`,
                value: String(value),
              };
            })}
          />
        </div>
      }
      title="Preview Dimensions"
      className="w-60 py-4"
      stayInPosition
      side="bottom"
      align={isOnCanvasNavbar ? "start" : "center"}
      alignOffset={isOnCanvasNavbar ? -10 : 0}
    >
      <DeprecatedButton
        variant="secondary"
        size="sm"
        endEnhancer={
          isOnCanvasNavbar ? (
            <ChevronDown
              size={12}
              className={twMerge(
                "text-muted transition-all",
                isPopoverOpen && "rotate-180",
              )}
            />
          ) : (
            <ChevronUp
              size={12}
              className={twMerge(
                "text-muted transition-all",
                isPopoverOpen && "rotate-180",
              )}
            />
          )
        }
      >
        <span className="text-muted">
          {isPreviewMode ? previewWidth : width}px
        </span>
      </DeprecatedButton>
    </Popover>
  );
};

// Utility Functions
function getCanvasIconComponent(canvas: EditorCanvas) {
  if (canvas === "mobile") {
    return Smartphone;
  }
  if (canvas === "tablet") {
    return Tablet;
  }
  return Monitor;
}
